import dayjs from "dayjs";
import IBoeModel, { IBoeLeaseComp, IBoePropertyInfo, IBoeSaleComp } from "../../interfaces/IBoeModel";
import IDeal from "../../interfaces/IDeal";
import ILead from "../../interfaces/ILead";
import type ILeaseComp from "../../interfaces/ILeaseComp";
import IPipedriveOrganization from "../../interfaces/IPipedriveOrganization";
import type IProperty from "../../interfaces/IProperty";
import type ISaleComp from "../../interfaces/ISaleComp";
import type { IPropertyAiGrade } from "../../services/propertyAiGradeApiService";
import { formatAddress } from "../../utils/addressFormat";
import type { IEstimates } from "../usePropertyEstimates";

export function buildBoeModelFromProperty(
    property: IProperty,
    estimates: IEstimates,
    aiGrade: IPropertyAiGrade,
    initials: string,
    pipedriveOrganizations: IPipedriveOrganization[]
): IBoeModel {
    const usableLand = aiGrade.usableArea;
    const storageArea = usableLand ? usableLand - (property.buildingSize / 43560) : null;
    const landRent = estimates?.monthlyRental ? (estimates.monthlyRental * 12) / 43560 : null;
    const purchasePrice = estimates?.salePricePerAcre && usableLand ? estimates.salePricePerAcre * usableLand : null;

    const propertyInfo = buildPropertyInfo(property, estimates);
    const leaseComps = buildBoeLeaseCompsFromEstimates(estimates, pipedriveOrganizations);
    const saleComps = buildBoeSaleCompsFromEstimates(estimates);

    const shortPropertyAddress = formatAddress(property);
    return {
        address: shortPropertyAddress,
        initials,
        propertyAddress: shortPropertyAddress,
        landSize: property.landSize,
        usableLand,
        storageArea,
        buildingSize: property.buildingSize,
        landRent,
        assessment: property.totalAssessment,
        purchasePrice,
        countyName: property.county,
        zoningType: property.zoning,
        analysisStartDate: calculateAnalysisStartDate(),
        screenshotBase64: aiGrade.bestScreenshot.base64,
        propertyInfo,
        leaseComps,
        saleComps,
    };
}

export function buildBoeModelFromDeal(
    deal: IDeal,
    property: IProperty,
    estimates: IEstimates,
    screenshotBase64: string,
    initials: string,
    pipedriveOrganizations: IPipedriveOrganization[],
): IBoeModel {
    const usableLand = deal.usableLand;
    const storageArea = usableLand ? usableLand - ((deal.buildingSize ?? 0) / 43560) : null;
    const landRent = deal.marketRentLand ?? (estimates.monthlyRental ? (estimates.monthlyRental * 12) / 43560 : null);
    const purchasePrice = deal.value;
    const assessment = property?.totalAssessment ?? null;

    const propertyInfo = buildPropertyInfo(property, estimates);
    const leaseComps = buildBoeLeaseCompsFromEstimates(estimates, pipedriveOrganizations);
    const saleComps = buildBoeSaleCompsFromEstimates(estimates);

    return {
        address: deal.address ?? "",
        propertyAddress: deal.address ?? "",
        initials,
        landSize: deal.landSizeAcres,
        usableLand,
        storageArea,
        buildingSize: deal.buildingSize,
        landRent,
        assessment,
        purchasePrice,
        countyName: deal.county ?? "",
        zoningType: deal.zoning,
        analysisStartDate: calculateAnalysisStartDate(),
        screenshotBase64,
        propertyInfo,
        leaseComps,
        saleComps,
    };
}

export function buildBoeModelFromLead(
    lead: ILead,
    property: IProperty,
    estimates: IEstimates,
    screenshotBase64: string,
    initials: string,
    pipedriveOrganizations: IPipedriveOrganization[],
): IBoeModel {
    const usableLand = lead.usableArea;
    const storageArea = usableLand ? usableLand - ((lead.buildingSize ?? 0) / 43560) : null;
    const landRent = estimates.monthlyRental ? (estimates.monthlyRental * 12) / 43560 : null;
    const purchasePrice = estimates.salePricePerAcre && usableLand ? estimates.salePricePerAcre * usableLand : null;

    const propertyInfo = buildPropertyInfo(property, estimates);
    const leaseComps = buildBoeLeaseCompsFromEstimates(estimates, pipedriveOrganizations);
    const saleComps = buildBoeSaleCompsFromEstimates(estimates);

    return {
        address: lead.address ?? "",
        propertyAddress: lead.address ?? "",
        initials,
        landSize: lead.landSize,
        usableLand,
        storageArea,
        buildingSize: lead.buildingSize,
        landRent,
        assessment: lead.totalAssessment,
        purchasePrice,
        countyName: property.county,
        zoningType: lead.zoning,
        analysisStartDate: calculateAnalysisStartDate(),
        screenshotBase64,
        propertyInfo,
        leaseComps,
        saleComps,
    };
}

export function getUserInitials(user: any): string {
    return `${user?.firstName?.[0]?.toUpperCase() ?? ""}${user?.lastName?.[0]?.toUpperCase() ?? ""}`;
}

function calculateAnalysisStartDate(): string {
    const today = dayjs();
    const plusNineMonths = today.add(9, "month");
    const firstOfNextMonth = plusNineMonths.add(1, "month").startOf("month");

    return firstOfNextMonth.toISOString();
}

function buildPropertyInfo(property: IProperty, estimates: IEstimates | null): IBoePropertyInfo {
    return {
        // Physical
        buildingSize: property.buildingSize ?? null,
        landSize: property.landSize ?? null,
        numberOfBuildings: property.numberOfBuildings ?? null,
        yearBuilt: property.yearBuilt ?? null,
        existingFAR: property.currentFar ? Math.round((property.currentFar + Number.EPSILON) * 100) / 100 : null,
        gradeChange: property.gradeChangeFeet ?? null,
        distanceToPower: property.distanceToPowerFeet ?? null,

        // Estimates
        monthlyRentalRate: estimates?.monthlyRental ?? null,
        salePricePerUsableAcre: estimates?.salePricePerAcre ?? null,
        salePricePerSqFt: estimates?.salePricePerSqFt ?? null,
        locationGrade: estimates?.locationGrade ?? null,

        // Summary
        parcelId: property.parcelId ?? null,
        county: property.county ?? null,
        municipality: property.municipality ?? property.city ?? null,
        ownershipStructure: property.ownershipStructure ?? null,
        zoning: property.zoning ?? null,
        floodplain: property.femaFloodZone ? `${property.femaFloodZone} (${property.femaFloodZoneSubtype})` : null,
        femaRiskRating: property.femaRiskRating ?? null,

        // Ownership
        ownerEntity: property.ownerEntity ?? null,
        ownerAddress: property.ownerAddress ?? null,
        saleDate: property.saleDate ? new Date(property.saleDate).toISOString() : null,
        salePrice: property.salePrice ?? null,
        pricePerSfLand: property.priceSFLand ?? null,
        pricePerAcreLand: property.priceAcreLand ?? null,

        // Demographics
        medianHouseholdIncome: property.medianHouseholdIncome ?? null,
        fiveYearHouseholdIncomeGrowth: property.householdIncomeGrowthNext5Years ?? null,
        housingAffordabilityIndex: property.housingAffordabilityIndex ?? null,
        populationDensity: property.populationDensity ?? null,
        past5YearsPopulationGrowth: property.populationGrowthPast5Years ?? null,
        next5YearsPopulationGrowth: property.populationGrowthNext5Years ?? null,
        past5YearsHousingGrowth: property.housingGrowthPast5Years ?? null,
        next5YearsHousingGrowth: property.housingGrowthNext5Years ?? null,

        // Assessment
        landAssessment: property.landAssessment ?? null,
        buildingAssessment: property.buildingAssessment ?? null,
        totalAssessment: property.totalAssessment ?? null,
    };
}

function leaseCompToBoeModel(
    comp: ILeaseComp,
    orgMap: Map<number, IPipedriveOrganization>,
    distance: number = 0,
    index: number | null = null
): IBoeLeaseComp {
    return {
        tenant: orgMap.get(comp.pipedriveCompanyId ?? -1)?.name ?? null,
        address: comp.address,
        zoning: comp.zoning,
        buildingSize: comp.buildingSize,
        tenantLandSize: comp.tenantLandSize,
        annualRent: comp.totalRent,
        dateSigned: comp.signedDate ? new Date(comp.signedDate).toISOString() : null,
        expiration: comp.leaseExpiration ? new Date(comp.leaseExpiration).toISOString() : null,
        monthlyRent: comp.totalRent / 12,
        landPerAcre: comp.landPsf,
        buildingPerPsf: comp.buildingPsf,
        distance,
        index,
    };
}

function buildBoeLeaseCompsFromEstimates(estimates: IEstimates, pipedriveOrganizations: IPipedriveOrganization[]): IBoeLeaseComp[] {
    const orgMap = new Map<number, IPipedriveOrganization>();
    pipedriveOrganizations.forEach(org => orgMap.set(org.id, org));

    return estimates.monthlyRentalComparableComps.comps.map(item =>
        leaseCompToBoeModel(item.comp, orgMap, item.distance, item.variance)
    );
}

function saleCompToBoeModel(comp: ISaleComp, distance: number = 0, index: number | null = null): IBoeSaleComp {
    return {
        salePrice: comp.salePrice,
        dateSold: new Date(comp.saleDate).toISOString(),
        address: comp.address,
        zoning: comp.zoning,
        buildingSize: comp.buildingSize,
        parcelSize: comp.parcelSizeAcres,
        landSizeUsable: comp.landSizeUsable,
        grossPerAcre: comp.grossPerAcre,
        pricePerUsableAcre: comp.pricePerUsableAcre,
        buildingPerSqFt: comp.buildingPerSqFt,
        distance,
        index,
    };
}

function buildBoeSaleCompsFromEstimates(estimates: IEstimates): IBoeSaleComp[] {
    return estimates.salePricePerAcreComparableComps.comps.map(item =>
        saleCompToBoeModel(item.comp, item.distance, item.variance)
    );
}

